import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, ClipboardList } from "lucide-react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { StatusBadge } from "./Shared";

export const GlobalSearch = () => {
  const { t, fmt } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); setOpen(false); return; }
    const timer = setTimeout(() => {
      api.get("/search", { params: { q } })
        .then((r) => { setResults(r.data); setOpen(true); })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const empty = results && results.customers.length === 0 && results.orders.length === 0;

  return (
    <div ref={boxRef} className="relative flex-1 max-w-md">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        placeholder={t("searchEverything")}
        className="w-full h-10 pl-9 pr-3 rounded-md border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:bg-white transition-colors"
      />
      {open && results && (
        <div data-testid="global-search-results" className="absolute top-12 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {empty && <div className="px-4 py-6 text-center text-sm text-slate-400">{t("noResults")}</div>}
          {results.customers.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-semibold text-slate-400">{t("customers")}</div>
              {results.customers.map((c) => (
                <button key={c.id} data-testid={`search-customer-${c.id}`} onClick={() => go(`/customers/${c.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left">
                  <User size={15} className="text-sky-600 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate uppercase">{[c.vehicles?.[0]?.vehicle, c.vehicles?.[0]?.plate, c.phone].filter(Boolean).join(" · ")}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {results.orders.length > 0 && (
            <div className="border-t border-slate-100">
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-semibold text-slate-400">{t("workOrders")}</div>
              {results.orders.map((o) => (
                <button key={o.id} data-testid={`search-order-${o.order_number}`} onClick={() => go(`/customers/${o.customer_id}`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left">
                  <ClipboardList size={15} className="text-slate-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{o.order_number} — {o.customer_name}</div>
                    <div className="text-xs text-slate-500 truncate uppercase">{[o.vehicle, o.plate].filter(Boolean).join(" · ")} · {fmt(o.total)}</div>
                  </div>
                  <StatusBadge status={o.status} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
